import { Formik } from "formik";
import { Search } from "lucide-react";
import { useMemo, useState } from "react";

import {
  type PhysicianNpiSearchResult,
  type SearchPhysiciansByNpiParams,
  useSearchPhysiciansByNpi,
  useVerifyPhysicianNpi,
} from "@elation/api-client/src/stitch-web";

import {
  FormikInput,
  FormikSelect,
  type FormikSelectOption,
  STATE_ITEMS,
} from "@src/shared/components/forms";
import { Text } from "@src/shared/components/general/Text";
import {
  HorizontalFlexbox,
  VerticalFlexbox,
} from "@src/shared/components/layout/Flexbox";
import { Button } from "@src/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@src/shared/components/ui/dialog";
import { Spinner } from "@src/shared/components/ui/spinner";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@src/shared/components/ui/tabs";

import {
  PhysicianNpiLookupFormValidationSchema,
  type PhysicianNpiLookupFormValues,
  PhysicianNpiSearchByNameFormValidationSchema,
  type PhysicianNpiSearchByNameFormValues,
} from "../../../../domain";

const ANY_STATE = "ANY";

const stateOptions: FormikSelectOption[] = [
  { label: "Any", value: ANY_STATE },
  ...STATE_ITEMS.map((item) => ({
    label: item.menuItemLabel,
    value: item.abbreviation,
  })),
];

export interface PhysicianNpiSearchDialogV2Props {
  open: boolean;
  initialFirstName?: string;
  initialLastName?: string;
  onOpenChange: (open: boolean) => void;
  onNpiVerified: (npi: string) => void | Promise<void>;
}

function resultDisplayName(result: PhysicianNpiSearchResult): string {
  return [
    result.namePrefix,
    result.firstName,
    result.middleName,
    result.lastName,
  ]
    .filter(Boolean)
    .join(" ");
}

function resultSubtitle(result: PhysicianNpiSearchResult): string {
  return [
    [result.practiceCity, result.practiceState].filter(Boolean).join(", "),
    `NPI ${result.npi}`,
  ]
    .filter(Boolean)
    .join(" · ");
}

export function PhysicianNpiSearchDialogV2({
  open,
  initialFirstName = "",
  initialLastName = "",
  onOpenChange,
  onNpiVerified,
}: PhysicianNpiSearchDialogV2Props) {
  const [searchParams, setSearchParams] =
    useState<SearchPhysiciansByNpiParams | null>(null);
  const [verifyError, setVerifyError] = useState<string | null>(null);

  const search = useSearchPhysiciansByNpi(searchParams ?? undefined, {
    query: {
      enabled: open && searchParams !== null,
    },
  });
  const verify = useVerifyPhysicianNpi();
  const results = search.data?.data.results ?? [];
  const isBusy = search.isFetching || verify.isPending;

  const searchByNameInitialValues = useMemo(
    () => ({
      firstName: initialFirstName,
      lastName: initialLastName,
      state: ANY_STATE,
    }),
    [initialFirstName, initialLastName],
  );

  const handleVerify = async (npi: string) => {
    setVerifyError(null);

    try {
      const response = await verify.mutateAsync({ data: { npi } });

      if (!response.data.valid) {
        setVerifyError("This NPI could not be verified.");
        return;
      }

      try {
        await onNpiVerified(response.data.npi);
      } catch {
        setVerifyError("NPI verified, but it could not be saved.");
        return;
      }

      onOpenChange(false);
    } catch {
      setVerifyError("Unable to verify this NPI.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        aria-label="Verify your NPI"
        className="flex max-h-[min(600px,90dvh)] w-[min(calc(100vw-2rem),440px)] max-w-none flex-col overflow-hidden rounded-[14px] p-5"
      >
        <DialogHeader className="space-y-0 text-left">
          <DialogTitle className="text-base">Verify your NPI</DialogTitle>
          <DialogDescription className="sr-only">
            Search the NPI registry by name or look up a specific NPI number.
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="name" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="name">Search by Name</TabsTrigger>
            <TabsTrigger value="npi">Search by NPI</TabsTrigger>
          </TabsList>

          <TabsContent value="name">
            <Formik<PhysicianNpiSearchByNameFormValues>
              enableReinitialize
              initialValues={searchByNameInitialValues}
              validationSchema={PhysicianNpiSearchByNameFormValidationSchema}
              onSubmit={(values) => {
                setVerifyError(null);
                setSearchParams({
                  firstName: values.firstName.trim(),
                  lastName: values.lastName.trim(),
                  state: values.state === ANY_STATE ? undefined : values.state,
                  limit: 10,
                });
              }}
            >
              {({ handleSubmit }) => (
                <VerticalFlexbox as="form" gap="md" onSubmit={handleSubmit}>
                  <HorizontalFlexbox
                    className="flex-col sm-stitch:flex-row"
                    gap="md"
                  >
                    <FormikInput
                      containerClassName="min-w-0 flex-1"
                      label="First name"
                      name="firstName"
                      placeholder="First name"
                      required
                    />
                    <FormikInput
                      containerClassName="min-w-0 flex-1"
                      label="Last name"
                      name="lastName"
                      placeholder="Last name"
                      required
                    />
                  </HorizontalFlexbox>
                  <FormikSelect
                    items={stateOptions}
                    label="State (optional)"
                    name="state"
                  />
                  <Button disabled={isBusy} type="submit">
                    {isBusy ? (
                      <Spinner className="mr-1" size="sm" />
                    ) : (
                      <Search />
                    )}
                    Search
                  </Button>
                </VerticalFlexbox>
              )}
            </Formik>
          </TabsContent>

          <TabsContent value="npi">
            <Formik<PhysicianNpiLookupFormValues>
              initialValues={{ npi: "" }}
              validationSchema={PhysicianNpiLookupFormValidationSchema}
              onSubmit={(values) => handleVerify(values.npi.trim())}
            >
              {({ handleSubmit }) => (
                <VerticalFlexbox as="form" gap="md" onSubmit={handleSubmit}>
                  <FormikInput
                    inputMode="numeric"
                    label="NPI Number"
                    name="npi"
                    placeholder="Enter your 10-digit NPI"
                  />
                  <Button disabled={isBusy} type="submit">
                    {isBusy ? (
                      <Spinner className="mr-1" size="sm" />
                    ) : (
                      <Search />
                    )}
                    Look Up
                  </Button>
                </VerticalFlexbox>
              )}
            </Formik>
          </TabsContent>
        </Tabs>

        {search.isError ? (
          <Text appearance="danger" role="alert" sizing="sm">
            Unable to search the NPI registry.
          </Text>
        ) : null}
        {verifyError || verify.isError ? (
          <Text appearance="danger" role="alert" sizing="sm">
            {verifyError ?? "Unable to verify this NPI."}
          </Text>
        ) : null}

        {searchParams && !search.isFetching ? (
          <VerticalFlexbox
            className="min-h-0 flex-1 overflow-y-auto pr-1"
            gap="sm"
          >
            {results.length > 0 ? (
              results.map((result) => (
                <Button
                  className="h-auto justify-start rounded-[10px] border border-border bg-background px-3 py-3 text-left hover:bg-muted"
                  key={result.npi}
                  onClick={() => {
                    void handleVerify(result.npi);
                  }}
                  type="button"
                  variant="ghost"
                >
                  <VerticalFlexbox gap="none">
                    <Text fontWeight={600} sizing="sm">
                      {resultDisplayName(result)}
                      {result.credential ? `, ${result.credential}` : ""}
                    </Text>
                    <Text appearance="subdued" sizing="xs">
                      {resultSubtitle(result)}
                    </Text>
                  </VerticalFlexbox>
                </Button>
              ))
            ) : (
              <Text appearance="subdued" sizing="sm">
                No matching individual providers found.
              </Text>
            )}
          </VerticalFlexbox>
        ) : null}

        <Button
          className="mx-auto h-auto p-0 text-xs underline"
          onClick={() => onOpenChange(false)}
          type="button"
          variant="link"
        >
          I don't have an NPI
        </Button>
      </DialogContent>
    </Dialog>
  );
}
